import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/sharp-solid-svg-icons"
import { Box, Skeleton, styled, Typography } from "@mui/material"
import {
  ChartsClipPath,
  ChartsReferenceLine,
  ChartsTooltipContainer,
  ChartsXAxis,
  ChartsYAxis,
} from "@mui/x-charts"
import {
  axisClasses,
  ChartZoomSlider,
  defaultSeriesConfigPro,
  useChartProApiRef,
  useChartProZoom,
  type HeatmapItemIdentifier,
  type HeatmapSeriesType,
  type HeatmapValueType,
} from "@mui/x-charts-pro"
import { ChartsContainerPro } from "@mui/x-charts-pro/ChartsContainerPro"
import { HEATMAP_PLUGINS, HeatmapPlot } from "@mui/x-charts-pro/Heatmap"
// @ts-expect-error heatmapSeriesConfig is not in public exports map (internal API)
import { heatmapSeriesConfig as _heatmapSeriesConfigRaw } from "@mui/x-charts-pro/Heatmap/seriesConfig"
import {
  useChartBrush,
  type ChartSeriesTypeConfig,
} from "@mui/x-charts/internals"
import type { WastewaterPCRActivityLevel } from "@phc/common/gen/threatAnalyticsGen"
import { enqueueSnackbar } from "notistack"
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { generatePath, Link } from "react-router"
import { FAQ_POST_SLUGS, ROUTES } from "../../utils/constants"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import { LightTooltip } from "../LocationDetails/LocationRisk/Shared"

import { PHCDateRangePicker } from "../Shared/PHCDateRangePicker"
import { FadeOverlay } from "./FadeOverlay"
import { HeatmapCell } from "./HeatmapCell"
import { HeatmapTooltip } from "./HeatmapTooltip"
import { StripedBackground } from "./StripedBackground"
import { useWastewaterData } from "./useWastewaterData"
import { useMonthIndices } from "./useWastewaterDates"
import { useWastewaterContext } from "./WastewaterContext"
import {
  CELL_GAP,
  CELL_WIDTH,
  ROW_HEIGHT,
  WASTEWATER_CHART_COLORS,
  WASTEWATER_HEATMAP_CELL_COLORS,
  WastewaterCategory,
  X_AXIS_HEIGHT,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_WIDTH,
  type HeatmapDataPointDetails,
} from "./wastewaterHeatmapConstants"
import {
  formatScientificString,
  getLevel,
  isRareDisease,
  makeAnomalyKey,
} from "./WastewaterUtils"
import { getDates } from "../../utils/dates"

const heatmapSeriesConfig =
  _heatmapSeriesConfigRaw as unknown as ChartSeriesTypeConfig<"heatmap">

const Container = styled("div")(() => ({
  width: "100%",
  background: extraColors.dark,
  color: extraColors.white,
}))

const Header = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
}))

const margin = { top: 0, bottom: 0, left: Y_AXIS_LABEL_OFFSET, right: 0 }

// [x, y, value, details]
export type ExtendedHeatmapItem = readonly [
  number,
  number,
  WastewaterCategory,
  HeatmapDataPointDetails,
]

export const WastewaterHeatmap = ({
  phcSiteId,
  limit,
  onHeightChange,
}: {
  phcSiteId: string
  limit: number
  onHeightChange?: (height: number) => void
}) => {
  const { dateRange, setDateRange, selectedAnomalyKey } = useWastewaterContext()

  const { activityLevels, anomalyKeys, isLoading, isError, refetch } =
    useWastewaterData({
      phcSiteId,
      limit,
      startDate: dateRange[0],
      endDate: dateRange[1],
    })

  const weekEnds = useMemo(() => {
    if (activityLevels.length === 0) return []

    const uniqueEndDates = Array.from(
      new Set(
        activityLevels.map(
          (item: WastewaterPCRActivityLevel) => item.sampling_end_date
        )
      )
    ).sort((a, b) => dayjsUTC(a).valueOf() - dayjsUTC(b).valueOf())

    // Build a regular weekly calendar grid
    const startDate = dayjsUTC(uniqueEndDates[0])
    const endDate = dayjsUTC(uniqueEndDates[uniqueEndDates.length - 1])

    const _weekEnds: ReturnType<typeof dayjsUTC>[] = []
    let cursor = startDate

    while (cursor.isBefore(endDate) || cursor.isSame(endDate, "day")) {
      _weekEnds.push(cursor)
      cursor = cursor.add(7, "day")
    }
    return _weekEnds
  }, [activityLevels])

  const { weekEndDatesForData, weekEndDatesForLabels, pathogens, heatmapData } =
    useMemo(() => {
      if (isLoading || activityLevels.length === 0) {
        return {
          weekEndDatesForData: [],
          weekEndDatesForLabels: [],
          pathogens: [],
          heatmapData: [],
        }
      }

      // Build analysis map, but bucket samples to their nearest week
      const analysisMap = new Map<string, WastewaterPCRActivityLevel>()

      if (weekEnds.length > 0) {
        activityLevels.forEach((analysis: WastewaterPCRActivityLevel) => {
          if (!analysis.target_name || !analysis.sampling_end_date) return

          const sampleDate = dayjsUTC(analysis.sampling_end_date)

          // Find the closest week bucket for this sample
          const closestWeek = weekEnds.reduce((closest, weekEnd) => {
            const diff = Math.abs(sampleDate.diff(weekEnd, "day"))
            const closestDiff = Math.abs(sampleDate.diff(closest, "day"))
            return diff < closestDiff ? weekEnd : closest
          })

          // Store by the week bucket, not the exact sample date
          const key = `${analysis.target_name}__${closestWeek.format("YYYY-MM-DD")}`

          // If multiple samples fall in same bucket, keep the most recent
          const existing = analysisMap.get(key)
          if (
            !existing?.sampling_end_date ||
            sampleDate.isAfter(dayjsUTC(existing.sampling_end_date))
          ) {
            analysisMap.set(key, analysis)
          }
        })
      }

      const points: ExtendedHeatmapItem[] = []

      const pathogenList = Array.from(
        new Set(
          activityLevels.map(
            (item: WastewaterPCRActivityLevel) => item.target_name
          )
        )
      ).sort()

      weekEnds.forEach((weekEnd, weekIndex) => {
        const dateKey = weekEnd.format("YYYY-MM-DD")
        pathogenList.forEach((pathogen, pathogenIndex) => {
          const analysis = analysisMap.get(`${pathogen}__${dateKey}`)

          // Handle missing data with clear indicators
          if (!analysis) {
            points.push([
              weekIndex,
              pathogenIndex,
              WastewaterCategory.NO_DATA,
              {
                sampleEndDate: "No sample",
                sampleSite: "",
                copies: "N/A",
                isAnomaly: false,
                isSelected: false,
                value: WastewaterCategory.NO_DATA,
              },
            ])
            return
          }

          const value = getLevel(analysis)
          const isRare = isRareDisease(analysis.target_name)
          const copiesValue = isRare
            ? analysis.fecal_normalized_target_copies_per_l
            : (analysis.fecal_normalized_target_copies_per_l_lod ?? null)

          points.push([
            weekIndex,
            pathogenIndex,
            value,
            {
              sampleEndDate: analysis.sampling_end_date,
              sampleSite: analysis.phc_site_id,
              copies:
                copiesValue != null
                  ? formatScientificString(copiesValue)
                  : "N/A",
              isAnomaly: anomalyKeys.has(
                makeAnomalyKey(analysis.target_name, analysis.sampling_end_date)
              ),
              isSelected:
                selectedAnomalyKey ===
                makeAnomalyKey(
                  analysis.target_name,
                  analysis.sampling_end_date
                ),
              value,
            },
          ])
        })
      })

      return {
        weekEndDatesForData: weekEnds,
        weekEndDatesForLabels: weekEnds.map(end => end.toDate()),
        pathogens: pathogenList,
        heatmapData: points,
      }
    }, [activityLevels, anomalyKeys, isLoading, selectedAnomalyKey, weekEnds])

  const { startIndices, monthCenters } = useMonthIndices(weekEndDatesForLabels)
  const apiRef = useChartProApiRef<"composition">()
  const headerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  const selectedHeatmapTooltipItem = useMemo<
    HeatmapItemIdentifier | undefined
  >((): HeatmapItemIdentifier | undefined => {
    const selectedItem = heatmapData.find(item => item[3].isSelected)
    return selectedAnomalyKey && selectedItem
      ? {
          seriesId: "heatmap",
          xIndex: selectedItem[0],
          yIndex: selectedItem[1],
          type: "heatmap",
        }
      : // undefined here is critical for tooltip to show up when no anomaly is selected,
        // null values will prevent tooltip from showing
        undefined
  }, [heatmapData, selectedAnomalyKey])

  const series: HeatmapSeriesType[] = useMemo(
    () => [
      {
        id: "heatmap",
        type: "heatmap",
        xAxisId: "weeksAxis",
        data: heatmapData as unknown as HeatmapValueType[],
      } as HeatmapSeriesType,
    ],
    [heatmapData]
  )

  const getAnomalyDetails = useCallback(
    (xIndex: number, yIndex: number): HeatmapDataPointDetails | undefined =>
      heatmapData.find(item => item[0] === xIndex && item[1] === yIndex)?.[3],
    [heatmapData]
  )

  const HeatmapCellWithAnomaly = useCallback(
    (props: Parameters<typeof HeatmapCell>[0]) => (
      <HeatmapCell {...props} getAnomalyDetails={getAnomalyDetails} />
    ),
    [getAnomalyDetails]
  )

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element || weekEndDatesForData.length === 0) return

    setContainerWidth(element.getBoundingClientRect().width)

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) {
        setContainerWidth(entry.contentRect.width)
      }
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [weekEndDatesForData.length])

  // Adjust zoom based on container width so cells are correct size and spacing
  useLayoutEffect(() => {
    if (containerWidth === 0 || !apiRef.current) return

    const drawingAreaWidth =
      containerWidth - Y_AXIS_WIDTH - margin.left - margin.right
    const totalWeeksWidth = weekEndDatesForData.length * (CELL_WIDTH + CELL_GAP)

    // Avoid division by zero or negative
    if (drawingAreaWidth <= 0 || totalWeeksWidth <= 0) return

    const visibleRatio = drawingAreaWidth / totalWeeksWidth
    const percentage = Math.min(100, visibleRatio * 100)

    const start = 100 - percentage
    const end = 100

    const rafId = requestAnimationFrame(() => {
      apiRef.current?.setZoomData([
        { axisId: "weeksAxis", start, end },
        { axisId: "labelAxis", start, end },
      ])
    })

    return () => cancelAnimationFrame(rafId)
  }, [containerWidth, apiRef, weekEndDatesForData.length])

  const chartHeight =
    pathogens.length * ROW_HEIGHT + margin.top + margin.bottom + X_AXIS_HEIGHT
  const loadingHeight = Math.max(chartHeight, 320)

  useLayoutEffect(() => {
    if (!onHeightChange) return
    const rafId = requestAnimationFrame(() => {
      const headerHeight =
        headerRef.current?.getBoundingClientRect().height ?? 0
      onHeightChange(chartHeight + headerHeight)
    })

    return () => cancelAnimationFrame(rafId)
  }, [chartHeight, onHeightChange])

  const chartWidth =
    weekEndDatesForData.length * (CELL_WIDTH + CELL_GAP) +
    Y_AXIS_WIDTH +
    margin.left +
    margin.right

  const loadingContent = <Skeleton height={loadingHeight} animation="wave" />

  const errorContent = (
    <Typography variant="body2" color={extraColors.medium}>
      Data is unavailable right now.
    </Typography>
  )

  const emptyContent = (
    <Typography variant="body2" color={extraColors.medium}>
      No data for the selected date range.
    </Typography>
  )

  const dataContent = (
    <ChartsContainerPro
      apiRef={apiRef}
      margin={margin}
      height={chartHeight}
      series={series}
      // @ts-expect-error heatmapSeriesConfig is an internal API; ChartSeriesConfig requires all series types but we only provide heatmap
      seriesConfig={{ ...defaultSeriesConfigPro, heatmap: heatmapSeriesConfig }}
      // @ts-expect-error HEATMAP_PLUGINS et al. don't satisfy ChartsContainerPro's internal plugin type signature
      plugins={[...HEATMAP_PLUGINS, useChartBrush, useChartProZoom]}
      zoomInteractionConfig={{
        // disable zoom
        zoom: [],
        pan: ["drag", "wheel"],
      }}
      xAxis={[
        {
          id: "weeksAxis",
          data: weekEndDatesForData.map((_, i) => i),
          scaleType: "band",
          categoryGapRatio: 0,
          disableTicks: true,
          disableLine: true,
          position: "top",
          zoom: true,
        },
        // centered month labels
        {
          id: "labelAxis",
          position: "bottom",
          scaleType: "linear",
          min: -0.5,
          max: weekEndDatesForData.length - 0.5,
          disableLine: true,
          tickSize: 0,
          valueFormatter: (value: number) => {
            const match = monthCenters.find(m => Math.abs(m.x - value) < 0.001)
            return match ? match.label : ""
          },
          zoom: true,
        },
      ]}
      yAxis={[
        {
          data: pathogens,
          scaleType: "band",
          categoryGapRatio: 0,
          width: Y_AXIS_WIDTH - Y_AXIS_LABEL_OFFSET,
          disableTicks: true,
          disableLine: true,
          label: "Pathogen",
          labelStyle: {
            color: WASTEWATER_CHART_COLORS.text,
            fontWeight: 600,
          },
          classes: { label: "pathogensAxis" },
          id: "pathogensAxis",
        },
      ]}
      zAxis={[
        {
          colorMap: {
            type: "ordinal",
            values: [
              WastewaterCategory.NO_DATA,
              WastewaterCategory.NOT_CALCULATED,
              WastewaterCategory.NOT_DETECTED,
              WastewaterCategory.LOW,
              WastewaterCategory.MEDIUM,
              WastewaterCategory.HIGH,
            ] satisfies WastewaterCategory[],
            colors: [
              WASTEWATER_HEATMAP_CELL_COLORS[WastewaterCategory.NO_DATA].main,
              WASTEWATER_HEATMAP_CELL_COLORS[WastewaterCategory.NOT_CALCULATED]
                .main,
              WASTEWATER_HEATMAP_CELL_COLORS[WastewaterCategory.NOT_DETECTED]
                .main,
              WASTEWATER_HEATMAP_CELL_COLORS[WastewaterCategory.LOW].main,
              WASTEWATER_HEATMAP_CELL_COLORS[WastewaterCategory.MEDIUM].main,
              WASTEWATER_HEATMAP_CELL_COLORS[WastewaterCategory.HIGH].main,
            ],
          },
        },
      ]}
      sx={{
        [`.${axisClasses.root}`]: {
          [`.${axisClasses.tickLabel}`]: {
            fill: WASTEWATER_CHART_COLORS.text,
            fontFamily: "Poppins",
            fontSize: 12,
          },
          [`.${axisClasses.label}`]: {
            fill: WASTEWATER_CHART_COLORS.text,
            fontFamily: "Poppins",
            fontSize: 14,
          },
          [`.${axisClasses.label}.pathogensAxis`]: {
            transform: `translateX(-${Y_AXIS_LABEL_OFFSET}px)`,
          },
        },
      }}
      tooltipItem={selectedHeatmapTooltipItem}
    >
      <ChartsClipPath id="heatmap-clip-path" />
      <StripedBackground numRows={pathogens.length} />
      <g clipPath="url(#heatmap-clip-path)">
        <HeatmapPlot slots={{ cell: HeatmapCellWithAnomaly }} />
        <g>
          {Array.from(startIndices)
            .filter(index => index > 0)
            .map(index => (
              <ChartsReferenceLine
                key={index}
                x={index}
                axisId="labelAxis"
                lineStyle={{ stroke: extraColors.disabled, strokeWidth: 1 }}
              />
            ))}
        </g>
        <FadeOverlay />
      </g>
      <ChartsXAxis
        axisId="labelAxis"
        tickInterval={monthCenters.map(m => m.x)}
      />
      <ChartsYAxis axisId="pathogensAxis" />
      <ChartsTooltipContainer trigger="item" position="top">
        <HeatmapTooltip heatmapData={heatmapData} pathogens={pathogens} />
      </ChartsTooltipContainer>
      <ChartZoomSlider />
    </ChartsContainerPro>
  )

  const content = isLoading
    ? loadingContent
    : isError
      ? errorContent
      : heatmapData.length > 0
        ? dataContent
        : emptyContent

  return (
    <Container>
      <Header ref={headerRef}>
        <Box
          sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}
        >
          <Link
            to={generatePath(ROUTES.FAQ_POST, {
              slug: FAQ_POST_SLUGS.WASTEWATER_METHODLOGY,
            })}
          >
            <LightTooltip
              title="Wastewater Heatmap Methodology"
              arrow
              placement="top-end"
            >
              <Box sx={{ cursor: "pointer" }}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{
                    fontSize: 16,
                    color: extraColors.status.navyLight,
                    paddingRight: 4,
                  }}
                />
              </Box>
            </LightTooltip>
          </Link>
          <Typography variant="h3Bold">Wastewater Heatmap</Typography>
        </Box>
        <Box sx={{ width: 350 }}>
          <PHCDateRangePicker
            showTime={false}
            initialValue={dateRange}
            onChange={dates => {
              setDateRange(getDates(dates))
              refetch().catch(() =>
                enqueueSnackbar("Error fetching wastewater data.", {
                  variant: "error",
                })
              )
            }}
            maxDate={dayjsUTC().endOf("week")}
          />
        </Box>
      </Header>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          maxWidth: chartWidth,
        }}
      >
        {content}
      </div>
    </Container>
  )
}
